import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setTransportationTypeIdAtom } from "../../../../../../atoms/actions/setTransportationTypeId/setTransportationTypeIdAtom";
import { CourseLayoutSelectorAtom } from "../../../../../../atoms/selectors/courseLayoutSelectorAtom/courseLayoutSelectorAtom";
import { TransportationTypeIdSelectorAtom } from "../../../../../../atoms/selectors/transportationTypeIdSelectorAtom/transportationTypeIdSelectorAtom";
import { TransportationTypeSelectNode$key } from "./__generated__/TransportationTypeSelectNode.graphql";

export const TransportationTypeSelect = ({
  fragmentRef,
}: {
  fragmentRef: TransportationTypeSelectNode$key | null;
}) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = useFragment(TransportationTypeSelectFragmentString, fragmentRef);

  const transportationTypes =
    data?.transportationTypes?.filter((tt) => !!tt) || [];

  const layout = useAtomValue(CourseLayoutSelectorAtom);

  const options = transportationTypes.flatMap((tt) => {
    if (!tt || !tt.enabled) return [];
    const rateForHoleCount =
      (layout?.holeCount === "EIGHTEEN"
        ? tt.eighteenHolePrice
        : tt.nineHolePrice) || 0;

    const transportationIcon = GetTransportIconFromCode(
      tt.customCode || tt?.code || "",
      "mainTiny"
    );

    return [
      {
        value: tt.id,
        label: (
          <HStack gap={2} alignItems="center">
            {transportationIcon ? transportationIcon({}) : null}
            <Text variant="mainSmallSemiBold">{tt.name}</Text>
            <Text variant="mainSmallSemiBold" color="text-secondary">
              {rateForHoleCount
                ? `${currencySymbol}${ConvertCentsToDollars(rateForHoleCount)}`
                : ""}
            </Text>
          </HStack>
        ),
      },
    ];
  });

  const currentTransportationTypeId = useAtomValue(
    TransportationTypeIdSelectorAtom
  );

  const setTransportationTypeId = useSetAtom(setTransportationTypeIdAtom);
  return (
    <DropdownSelect
      testID="transportation-select"
      hideOuterMargin
      value={currentTransportationTypeId}
      options={options}
      onChange={(value) => {
        if (value) setTransportationTypeId(value);
      }}
    />
  );
};

const TransportationTypeSelectFragmentString = graphql`
  fragment TransportationTypeSelectNode on Slot
  @argumentDefinitions(memberId: { type: "ID", defaultValue: null }) {
    transportationTypes(memberId: $memberId, memberVisible: true) {
      id
      code
      customCode
      name
      enabled
      nineHolePrice: totalPrice(holeCount: NINE)
      eighteenHolePrice: totalPrice(holeCount: EIGHTEEN)
    }
  }
`;
